import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  DollarSign, Wallet, Shield, Activity, Building2, AlertCircle,
  RefreshCw, ArrowUpRight, ArrowDownRight, Landmark, Sparkles, ChevronRight,
} from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { toast } from 'sonner';
import PlaidLinkButton from '../components/PlaidLinkButton';
import { WealthMonarch } from '../components/WealthMonarch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const money = (v, dec = 2) =>
  v == null ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;

const HeroCard = ({ label, value, sub, icon: Icon, color, testId }) => (
  <div className={`glass-card p-6 border ${color.border} ${color.bg} relative overflow-hidden`}>
    <div className="relative z-10">
      <div className="flex items-center justify-between mb-3">
        <span className={`text-xs uppercase tracking-widest font-bold ${color.text}`}>{label}</span>
        <Icon className={`w-5 h-5 ${color.text}`} />
      </div>
      <p className="text-3xl font-bold font-mono text-white mb-1" data-testid={testId}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  </div>
);

const WealthDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [live, setLive] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [guardian, setGuardian] = useState(null);
  const [story, setStory] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [flowTargetInput, setFlowTargetInput] = useState('');
  const navigate = useNavigate();

  const refreshGuardian = async () => {
    try {
      const g = await axios.get(`${API}/guardian`, { headers: getAuthHeaders() });
      setGuardian(g.data);
    } catch (ge) { /* guardian optional */ }
  };

  const setVerdict = async (merchant, verdict, monthlyEst) => {
    try {
      await axios.post(`${API}/guardian/leaks/verdict`,
        { merchant, verdict, monthly_est: monthlyEst }, { headers: getAuthHeaders() });
      toast.success(`Verdict saved: ${verdict} — ${merchant.slice(0, 30)}`);
      refreshGuardian();
    } catch (e) { toast.error('Verdict failed'); }
  };

  const saveFlowTarget = async () => {
    const v = parseFloat(flowTargetInput);
    if (isNaN(v)) { toast.error('Enter a number (e.g. 0 for breakeven, 3000 for +$3k/mo)'); return; }
    try {
      await axios.post(`${API}/guardian/flow-target`,
        { target_net_flow_per_month: v }, { headers: getAuthHeaders() });
      toast.success('Flow target set');
      setFlowTargetInput('');
      refreshGuardian();
    } catch (e) { toast.error('Failed to set target'); }
  };

  const fetchLive = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/wealth/live`, { headers: getAuthHeaders() });
      setLive(res.data);
      axios.get(`${API}/wealth/story`, { headers: getAuthHeaders() })
        .then((r) => setStory(r.data)).catch(() => {});
      try {
        const g = await axios.get(`${API}/guardian`, { headers: getAuthHeaders() });
        setGuardian(g.data);
      } catch (ge) { /* guardian optional — wealth still renders */ }
    } catch (e) {
      toast.error('Failed to load live wealth data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLive(); }, [fetchLive]);

  const syncAll = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/plaid/refresh`, {}, { headers: getAuthHeaders() });
      setReceipt(r.data);
      toast.success(`All accounts updated — ${r.data.accounts_refreshed} accounts, ${r.data.transactions_synced} transactions${r.data.guardian_recomputed ? ', guardian refreshed' : ''}`);
      await fetchLive();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        </div>
      </Layout>
    );
  }

  const w = live?.wealth || {};
  const nw = w.net_worth || {};
  const isLive = nw.confidence === 'live';
  const institutions = live?.institutions || [];
  const anomalies = w.anomalies || [];

  return (
    <Layout>
      <div className="space-y-8" data-testid="wealth-dashboard">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2 flex items-center gap-3">
              WEALTH
              {isLive ? (
                <span data-testid="wealth-live-badge" className="text-[11px] font-mono uppercase px-2 py-1 rounded bg-green-500/20 text-green-400 border border-green-500/30">
                  LIVE
                </span>
              ) : (
                <span data-testid="wealth-unconnected-badge" className="text-[11px] font-mono uppercase px-2 py-1 rounded bg-zinc-500/20 text-zinc-400 border border-zinc-500/30">
                  {nw.confidence || 'no data'}
                </span>
              )}
            </h1>
            <p className="text-muted-foreground text-sm" data-testid="wealth-freshness">
              {isLive && nw.as_of
                ? `Real bank data via Plaid (read-only) · as of ${new Date(nw.as_of).toLocaleString()}`
                : 'Connect a bank to see real figures — nothing here is estimated'}
              {' · '}{live?.connections_used ?? 0} of {live?.connection_limit ?? 10} trial connections used
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={syncAll}
              disabled={syncing}
              className="bg-primary hover:opacity-90 text-black font-bold px-5 py-3 flex items-center gap-2 transition-opacity disabled:opacity-50 rounded"
              data-testid="sync-all-button"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              <span className="text-sm font-bold">{syncing ? 'Updating all accounts…' : 'Update All Accounts'}</span>
            </button>
            <PlaidLinkButton onSuccess={fetchLive} buttonText="Connect Bank" />
          </div>
        </div>

        {/* Bank re-login needed */}
        {(live?.items_needing_login || []).length > 0 && (
          <div className="glass-card p-4 border border-amber-500/40 bg-amber-500/10 flex items-center justify-between gap-3 flex-wrap" data-testid="relink-banner">
            <p className="text-sm text-amber-300 font-semibold flex-1 min-w-[240px]">
              ⚠ {live.items_needing_login.map((i) => i.institution_name || 'A bank').join(', ')} needs a quick re-login so LEE can keep pulling fresh numbers — takes ~30 seconds in Plaid's secure window.
            </p>
            {live.items_needing_login.map((i) => (
              <PlaidLinkButton key={i.item_id} relinkItemId={i.item_id}
                buttonText={`Reconnect ${i.institution_name || 'bank'}`} onSuccess={syncAll}
                buttonClassName="bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs px-4 py-2.5 rounded flex items-center gap-2" />
            ))}
          </div>
        )}

        {/* MONEY STORY — plain English, one action */}
        {story && (
          <div className="magic-in rounded-3xl border border-primary/25 bg-gradient-to-br from-primary/[0.07] via-transparent to-emerald-500/[0.05] p-6 sm:p-7" data-testid="money-story">
            <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-bold flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Your money, in plain words
            </p>
            <h2 className="text-xl sm:text-2xl font-bold text-white mt-3 leading-snug" data-testid="story-headline">{story.headline}</h2>
            <div className="flex flex-wrap gap-2 mt-4">
              <span className="px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-sm font-mono" data-testid="story-in-chip">↑ {money(story.came_in, 0)} in</span>
              <span className="px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/25 text-red-300 text-sm font-mono" data-testid="story-out-chip">↓ {money(story.went_out, 0)} out</span>
              <span className={`px-3 py-1.5 rounded-full text-sm font-mono border ${story.left_over >= 0 ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300' : 'bg-yellow-500/10 border-yellow-500/25 text-yellow-300'}`} data-testid="story-left-chip">
                {story.left_over >= 0 ? '+' : '−'}{money(Math.abs(story.left_over), 0)} left
              </span>
              {story.days_left != null && <span className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-zinc-400 text-sm">{story.days_left} days left this month</span>}
            </div>
            <p className={`mt-4 text-sm flex items-start gap-2 ${story.safe_level === 'danger' ? 'text-red-300' : story.safe_level === 'safe' ? 'text-emerald-300' : 'text-zinc-300'}`} data-testid="story-safe-line">
              <Shield className="w-4 h-4 mt-0.5 shrink-0" /> {story.safe_line}
            </p>
            {story.pace_line && <p className="mt-2 text-sm text-amber-300" data-testid="story-pace-line">{story.pace_line}</p>}
            {story.one_thing && (
              <div className="mt-5 rounded-2xl bg-black/30 border border-white/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="story-one-thing">
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-primary font-bold">The one thing to do</p>
                  <p className="text-white font-bold mt-0.5">{story.one_thing.title}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">{story.one_thing.why}</p>
                </div>
                <button onClick={() => story.one_thing.link === '/wealth' ? window.scrollTo({ top: 700, behavior: 'smooth' }) : navigate(story.one_thing.link)}
                  className="shrink-0 px-5 py-2.5 rounded-full bg-primary text-black font-bold text-sm flex items-center gap-1.5 hover:opacity-90 transition-opacity" data-testid="story-cta">
                  {story.one_thing.cta} <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Sync receipt */}
        {receipt && (
          <div data-testid="sync-receipt" className="glass-card p-4 border border-green-500/30 bg-green-500/5 text-sm text-zinc-200 font-mono">
            Update receipt — {receipt.accounts_refreshed} accounts refreshed · {receipt.transactions_synced} transactions synced · {receipt.liabilities_synced} liability report(s){receipt.guardian_recomputed ? ' · guardian recomputed' : ''} · {new Date(receipt.timestamp).toLocaleString()}
          </div>
        )}

        {/* Hero metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <HeroCard label="Net Worth" value={money(nw.value)} icon={DollarSign} testId="total-net-worth"
            sub={isLive ? `LIVE · ${nw.as_of ? new Date(nw.as_of).toLocaleString() : ''}` : 'no live data yet'}
            color={{ border: 'border-primary/30', bg: 'bg-primary/5', text: 'text-primary' }} />
          <HeroCard label="Cash on Hand" value={money(w.cash_on_hand)} icon={Wallet} testId="cash-on-hand"
            sub="checking + savings" color={{ border: 'border-blue-500/30', bg: 'bg-blue-500/5', text: 'text-blue-400' }} />
          <HeroCard label="Debts" value={money(w.debts_total)} icon={Shield} testId="debts-total"
            sub="credit + loan balances" color={{ border: 'border-red-500/30', bg: 'bg-red-500/5', text: 'text-red-400' }} />
          <HeroCard label="Runway" value={w.runway_months != null ? `${w.runway_months} mo` : '—'} icon={Activity} testId="runway-months"
            sub={w.monthly_burn != null ? `at ${money(w.monthly_burn, 0)}/mo operating burn (transfers excluded)` : 'needs transaction history'}
            color={{ border: 'border-yellow-500/30', bg: 'bg-yellow-500/5', text: 'text-yellow-400' }} />
        </div>

        {/* Monarch-style live analytics */}
        <WealthMonarch key={refreshKey} />

        {/* Cash flow */}
        {(w.monthly_inflow != null || w.monthly_burn != null) && (
          <div className="glass-card p-6 border border-white/10" data-testid="cash-flow-card">
            <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
              Monthly Cash Flow {w.cash_flow_basis ? `(${w.cash_flow_basis.days_covered}-day window, own-account transfers excluded)` : '(live transactions)'}
            </span>
            <div className="flex flex-wrap gap-8 mt-3">
              <div className="flex items-center gap-2">
                <ArrowUpRight className="w-4 h-4 text-green-500" />
                <span className="font-mono text-white" data-testid="monthly-inflow">{money(w.monthly_inflow, 0)}</span>
                <span className="text-xs text-muted-foreground">in</span>
              </div>
              <div className="flex items-center gap-2">
                <ArrowDownRight className="w-4 h-4 text-red-400" />
                <span className="font-mono text-white" data-testid="monthly-outflow">{money(w.monthly_burn, 0)}</span>
                <span className="text-xs text-muted-foreground">out</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`font-mono ${((w.net_cash_flow ?? 0) >= 0) ? 'text-green-400' : 'text-red-400'}`} data-testid="net-cash-flow">
                  {(w.net_cash_flow ?? 0) >= 0 ? '+' : '−'}{money(Math.abs(w.net_cash_flow ?? 0), 0)}
                </span>
                <span className="text-xs text-muted-foreground">net / month</span>
              </div>
            </div>
            {w.cash_flow_basis && (
              <p className="text-[11px] text-muted-foreground mt-3 font-mono" data-testid="cash-flow-basis">
                math: {w.cash_flow_basis.transactions_used} txns {w.cash_flow_basis.window_start} → {w.cash_flow_basis.window_end} ÷ {w.cash_flow_basis.months_divisor} mo · transfers excluded: {money(w.cash_flow_basis.transfers_excluded_in, 0)} in / {money(w.cash_flow_basis.transfers_excluded_out, 0)} out
              </p>
            )}
          </div>
        )}

        {/* ===== FINANCIAL GUARDIAN — THE FLOOR ===== */}
        {guardian?.floor?.data_ready && (
          <div className={`glass-card p-6 border ${guardian.floor.runway_alarm?.triggered ? 'border-red-500/60 bg-red-500/10' : 'border-emerald-500/30 bg-emerald-500/5'}`} data-testid="guardian-floor">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <span className={`text-xs uppercase tracking-widest font-bold ${guardian.floor.runway_alarm?.triggered ? 'text-red-400' : 'text-emerald-400'}`}>
                <Shield className="w-4 h-4 inline mr-1 -mt-0.5" />Financial Guardian — The Floor
              </span>
              {guardian.floor.runway_alarm?.triggered ? (
                <span className="text-xs font-bold px-2 py-1 rounded bg-red-500 text-white animate-pulse" data-testid="guardian-p1-alarm">🚨 P1 — RUNWAY BELOW 3 MONTHS</span>
              ) : (
                <span className="text-xs font-bold px-2 py-1 rounded border border-emerald-500/40 text-emerald-400" data-testid="guardian-floor-ok">FLOOR INTACT</span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Emergency Reserve ({guardian.floor.reserve_target_months} mo of true spend)</p>
                <p className="text-xl font-mono text-white" data-testid="guardian-reserve">
                  {money(guardian.floor.reserve_current, 0)} <span className="text-muted-foreground text-sm">of {money(guardian.floor.reserve_target, 0)}</span>
                </p>
                <div className="h-2 rounded bg-white/10 mt-2 overflow-hidden">
                  <div className={`h-full ${guardian.floor.reserve_funded ? 'bg-emerald-500' : 'bg-yellow-500'}`}
                    style={{ width: `${Math.min(guardian.floor.reserve_progress_pct || 0, 100)}%` }} />
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">{guardian.floor.reserve_progress_pct}% — {guardian.floor.reserve_funded ? 'FUNDED' : 'building'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Risk Capital (spendable without touching floor)</p>
                <p className="text-xl font-mono text-white" data-testid="guardian-risk-capital">{money(guardian.floor.risk_capital, 0)}</p>
                <p className="text-[11px] text-muted-foreground mt-1 font-mono">{guardian.floor.risk_capital_math}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">True Runway (P1 alarm &lt; 3 mo)</p>
                <p className="text-xl font-mono text-white">{guardian.floor.runway_months} mo</p>
                <p className="text-[11px] text-muted-foreground mt-1">at {money(guardian.floor.true_monthly_expenses, 0)}/mo true expenses</p>
              </div>
            </div>
            {guardian.floor.runway_alarm?.triggered && (
              <div className="mt-4 pt-4 border-t border-red-500/30 text-sm space-y-1" data-testid="guardian-p1-detail">
                <p className="text-red-300 font-bold">Causes:</p>
                {(guardian.floor.runway_alarm.causes || []).map((c, i) => <p key={i} className="text-red-200 text-xs">• {c}</p>)}
                <p className="text-red-300 font-bold mt-2">Options:</p>
                {(guardian.floor.runway_alarm.options || []).map((o, i) => <p key={i} className="text-red-200 text-xs">• {o}</p>)}
              </div>
            )}
          </div>
        )}

        {/* Leaks */}
        {guardian?.leaks && (guardian.leaks.recurring?.length > 0 || guardian.leaks.category_jumps?.length > 0) && (
          <div className="glass-card p-6 border border-white/10" data-testid="guardian-leaks">
            <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Leak Detection — recurring charges ({money(guardian.leaks.recurring_total_monthly_est, 0)}/mo inventoried)</span>
            <div className="mt-3 space-y-1.5">
              {(guardian.leaks.recurring || []).slice(0, 8).map((r, i) => (
                <div key={i} className="flex justify-between items-center gap-2 text-sm font-mono flex-wrap" data-testid={`leak-row-${i}`}>
                  <span className="text-zinc-300 truncate mr-1">{r.merchant} <span className="text-muted-foreground text-xs">×{r.occurrences}</span>
                    {r.verdict && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ml-2 ${r.verdict === 'cancel' ? 'bg-red-500/20 text-red-400' : r.verdict === 'keep' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                        {r.verdict.toUpperCase()}
                      </span>
                    )}
                  </span>
                  <span className="text-white flex items-center gap-2 flex-wrap min-w-0">
                    <span className="whitespace-nowrap">{money(r.monthly_est, 0)}/mo</span>
                    {r.flags?.length > 0 && <span className="text-yellow-400 text-xs truncate max-w-[140px]">⚠ {r.flags[0]}</span>}
                    <span className="flex gap-1 shrink-0">
                      <button onClick={() => setVerdict(r.merchant, 'keep', r.monthly_est)} data-testid={`leak-keep-${i}`}
                        className="text-[10px] px-1.5 py-0.5 rounded border border-green-500/40 text-green-400 hover:bg-green-500/10">keep</button>
                      <button onClick={() => setVerdict(r.merchant, 'cancel', r.monthly_est)} data-testid={`leak-cancel-${i}`}
                        className="text-[10px] px-1.5 py-0.5 rounded border border-red-500/40 text-red-400 hover:bg-red-500/10">cancel</button>
                      <button onClick={() => setVerdict(r.merchant, 'investigate', r.monthly_est)} data-testid={`leak-investigate-${i}`}
                        className="text-[10px] px-1.5 py-0.5 rounded border border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10">?</button>
                    </span>
                  </span>
                </div>
              ))}
            </div>
            {(guardian.leaks.category_jumps || []).length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-xs text-yellow-400 font-bold uppercase tracking-wider mb-1">Category jumps &gt;25% MoM</p>
                {(guardian.leaks.category_jumps || []).slice(0, 4).map((j, i) => (
                  <p key={i} className="text-xs text-yellow-300 font-mono">⚠ {j.category}: {money(j.prev_total, 0)} ({j.prev_month}) → {money(j.last_total, 0)} ({j.last_month}) = +{j.jump_pct}%</p>
                ))}
              </div>
            )}
            {guardian.leaks.verdict_summary && (guardian.leaks.verdict_summary.canceled > 0 || guardian.leaks.verdict_summary.kept > 0 || guardian.leaks.verdict_summary.investigating > 0) && (
              <p className="text-xs text-emerald-400 font-mono mt-3" data-testid="leak-verdict-summary">
                Verdicts: {guardian.leaks.verdict_summary.canceled} cancel (~{money(guardian.leaks.verdict_summary.intended_monthly_savings, 0)}/mo intended savings) · {guardian.leaks.verdict_summary.kept} keep · {guardian.leaks.verdict_summary.investigating} investigating
              </p>
            )}
            <p className="text-[11px] text-muted-foreground mt-3">{guardian.leaks.note}</p>
          </div>
        )}

        {/* Idle cash */}
        {guardian?.idle_cash?.flagged_amount > 0 && (
          <div className="glass-card p-6 border border-blue-500/30 bg-blue-500/5" data-testid="guardian-idle-cash">
            <span className="text-xs uppercase tracking-widest text-blue-400 font-bold">Idle Cash Flag — {money(guardian.idle_cash.flagged_amount, 0)} earning ~0%</span>
            <p className="text-sm text-zinc-300 mt-2">{guardian.idle_cash.detail}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              {(guardian.idle_cash.options || []).map((o, i) => (
                <div key={i} className="border border-white/10 rounded p-3">
                  <p className="text-sm text-white font-bold">{o.name}</p>
                  <p className="text-xs text-green-400 mt-1">+ {o.pros}</p>
                  <p className="text-xs text-red-400 mt-1">− {o.cons}</p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">{guardian.idle_cash.disclaimer}</p>
          </div>
        )}

        {/* ===== THE LADDER ===== */}
        {guardian?.milestone?.data_ready && (
          <div className="glass-card p-6 border border-primary/30 bg-primary/5" data-testid="guardian-milestone">
            <span className="text-xs uppercase tracking-widest text-primary font-bold">Ladder to $1,000,000</span>
            <div className="flex flex-wrap items-baseline gap-4 mt-2">
              <p className="text-2xl font-mono text-white" data-testid="milestone-current">{money(guardian.milestone.current_net_worth, 0)}</p>
              <p className="text-sm text-muted-foreground">{guardian.milestone.progress_pct}% · gap {money(guardian.milestone.gap, 0)}</p>
              {guardian.milestone.trend_30d != null && (
                <p className={`text-sm font-mono ${guardian.milestone.trend_30d >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  30d: {guardian.milestone.trend_30d >= 0 ? '+' : ''}{money(guardian.milestone.trend_30d, 0)}
                </p>
              )}
            </div>
            <div className="h-2 rounded bg-white/10 mt-3 overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${Math.min(guardian.milestone.progress_pct || 0, 100)}%` }} />
            </div>
            <p className="text-sm text-zinc-300 mt-3" data-testid="milestone-trajectory">{guardian.milestone.trajectory}</p>
            <div className="flex flex-wrap gap-4 mt-3">
              {(guardian.milestone.required_rates || []).map((r, i) => (
                <div key={i} className="text-xs font-mono text-muted-foreground border border-white/10 rounded px-3 py-2">
                  $1M in {r.reach_target_in_years} yrs → needs <span className="text-white">+{money(r.needed_net_flow_per_month, 0)}/mo</span> net
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">{guardian.milestone.honesty}</p>
            {guardian.flow_fix && guardian.flow_fix.current_net_flow != null && (
              <div className="mt-4 pt-4 border-t border-white/10" data-testid="guardian-flow-fix">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-1">Flow Fix Plan</p>
                <p className="text-sm font-mono text-white">
                  target {guardian.flow_fix.target_net_flow_per_month >= 0 ? '+' : ''}{money(guardian.flow_fix.target_net_flow_per_month, 0)}/mo
                  {guardian.flow_fix.is_default_target && <span className="text-muted-foreground text-xs"> (breakeven default)</span>}
                  {' · '}current <span className={guardian.flow_fix.current_net_flow >= 0 ? 'text-green-400' : 'text-red-400'}>
                    {guardian.flow_fix.current_net_flow >= 0 ? '+' : '−'}{money(Math.abs(guardian.flow_fix.current_net_flow), 0)}/mo
                  </span>
                  {' · '}{guardian.flow_fix.on_target ? <span className="text-green-400">ON TARGET ✓</span> : <span className="text-yellow-400">gap {money(guardian.flow_fix.gap_to_target, 0)}/mo</span>}
                </p>
                <div className="flex gap-2 mt-2 items-center">
                  <input type="number" value={flowTargetInput} onChange={(e) => setFlowTargetInput(e.target.value)}
                    placeholder="set monthly target (0 = breakeven)" data-testid="flow-target-input"
                    className="bg-black/40 border border-white/20 rounded px-2 py-1 text-xs font-mono text-white w-56" />
                  <button onClick={saveFlowTarget} data-testid="flow-target-save"
                    className="text-xs px-3 py-1 rounded border border-primary/40 text-primary hover:bg-primary/10">Set target</button>
                </div>
              </div>
            )}
            {guardian.weekly_review && (
              <div className="mt-4 pt-4 border-t border-white/10" data-testid="guardian-weekly-review">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-1">This week's honest call</p>
                <p className="text-sm text-white">{guardian.weekly_review.honest_call}</p>
              </div>
            )}
          </div>
        )}

        {/* Anomalies */}
        {anomalies.length > 0 && (
          <div className="glass-card p-6 border border-amber-500/30 bg-amber-500/5" data-testid="wealth-anomalies">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-4 h-4 text-amber-400" />
              <span className="text-xs uppercase tracking-widest text-amber-400 font-bold">Anomalies · last 7 days (≥ $500)</span>
            </div>
            <div className="space-y-1.5">
              {anomalies.map((a, i) => (
                <div key={i} className="text-sm text-zinc-300 font-mono">{a.detail}</div>
              ))}
            </div>
          </div>
        )}

        {/* Institutions & accounts */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Landmark className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-bold text-white uppercase tracking-tight">
              Connected Institutions · {institutions.length}
            </h2>
          </div>
          {institutions.length === 0 ? (
            <div className="glass-card p-8 border border-white/10 text-center">
              <Building2 className="w-10 h-10 text-primary mx-auto mb-3" />
              <p className="text-white font-semibold mb-1">No banks connected yet</p>
              <p className="text-sm text-muted-foreground mb-4">Your login goes into Plaid's secure window — never into LEE.</p>
              <div className="flex justify-center"><PlaidLinkButton onSuccess={fetchLive} /></div>
            </div>
          ) : (
            <div className="space-y-4">
              {institutions.map((inst) => (
                <div key={inst.item_id} data-testid={`institution-${inst.item_id}`} className="glass-card p-6 border border-white/10">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                    <div className="text-white font-semibold">{inst.institution_name || 'Institution'}</div>
                    <div className="text-xs text-muted-foreground font-mono" data-testid={`institution-sync-${inst.item_id}`}>
                      {inst.account_count} account{inst.account_count !== 1 ? 's' : ''} · Last synced: {inst.last_sync ? new Date(inst.last_sync).toLocaleString() : 'Never'}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {(inst.accounts || []).map((a, i) => (
                      <div key={i} className="bg-secondary/20 border border-border rounded p-4 flex items-center justify-between">
                        <div>
                          <div className="text-sm text-white">{a.account_name} <span className="text-muted-foreground">···{a.mask}</span></div>
                          <div className="text-[11px] uppercase text-muted-foreground">{a.account_subtype || a.account_type}</div>
                        </div>
                        <div className="text-right">
                          <div className={`font-mono ${((a.account_type || '') === 'credit' || (a.account_type || '') === 'loan') ? 'text-red-400' : 'text-white'}`}>
                            {money(a.balance_current)}
                          </div>
                          {a.balance_available != null && (
                            <div className="text-[11px] text-muted-foreground font-mono">avail {money(a.balance_available)}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {live?.transactions_stored > 0 && (
            <p className="text-xs text-muted-foreground mt-3 font-mono" data-testid="transactions-stored">
              {live.transactions_stored} transactions stored for cash-flow analysis
            </p>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default WealthDashboard;
